import { create } from 'apisauce';

import { formatFormData } from '@/shared/lib/data';

import { IAdminErrorResponse } from '@/shared/types/admin-api';

export type TAdminApiFieldErrors = Record<string, string[]>;

export const adminApiWrapper = create({
  baseURL: '/api/admin/v1/',
  headers: {
    'Content-Type': 'application/json'
  },
  transformRequest: [
    (data, headers) => {
      if (headers['Content-Type'] === 'multipart/form-data') {
        return formatFormData(data);
      }

      return JSON.stringify(data);
    }
  ]
});

export const ADMIN_API_URLS = {
  auth: {
    password: 'auth/password',
    sign_in: 'auth/sign_in',
    sign_out: 'auth/sign_out'
  },
  dictionaries: {
    interests: 'interests',
    languages: 'languages',
    skills: 'skills'
  },
  menuCounters: 'menu_counters',
  platformSettings: 'platform_settings'
} as const;

/** Ключи счётчиков меню админки; расширяется вместе с ответом GET menu_counters */
export type TAdminMenuCounterKey =
  | 'actor_complaints_new'
  | 'feedbacks_new'
  | 'imported_films_stuck'
  | 'imported_films_unf_review'
  | 'participant_complaints_new';

/** Готовая человеческая фраза бэка: с заглавной кириллической буквы («У актёра уже есть открытое дело») */
const HUMAN_PHRASE_PATTERN = /^[А-ЯЁ]/;

/**
 * Сообщения поля списком строк. Ошибки вложенных записей (nested attributes,
 * `{ actor_awards: { year: [...] } }`) разворачиваются до самих фраз
 * @param messages Сообщения поля: строка, массив или объект вложенных полей
 */
const flattenAdminMessages = (messages: unknown): string[] => {
  if (Array.isArray(messages)) return messages.flatMap(flattenAdminMessages);
  if (messages && typeof messages === 'object') {
    return Object.values(messages).flatMap(flattenAdminMessages);
  }

  return [String(messages)];
};

/**
 * Сообщения одного поля: готовая фраза бэка показывается как есть, а технический
 * текст валидации («can't be blank», «не может быть пустым») — с именем поля
 * @param field Имя поля
 * @param messages Сообщения
 */
const formatAdminFieldMessages = (field: string, messages: unknown) => {
  const list = flattenAdminMessages(messages);

  return list.every(message => HUMAN_PHRASE_PATTERN.test(message))
    ? list.join(' ')
    : `${field}: ${list.join(', ')}`;
};

/**
 * Преобразует ответ ошибки admin API в строку.
 * @param data Данные ошибки
 */
export const getAdminApiErrorMessage = (data?: unknown) => {
  if (!data || typeof data !== 'object' || !('errors' in data)) {
    return 'Не удалось выполнить действие';
  }

  const errorData = data as IAdminErrorResponse;

  if (!errorData.errors) return 'Не удалось выполнить действие';

  if (Array.isArray(errorData.errors)) return errorData.errors.join(' ');

  return Object.entries(errorData.errors)
    .map(([field, messages]) => formatAdminFieldMessages(field, messages))
    .join(' ');
};

/**
 * Возвращает field-level ошибки admin API.
 * @param data Данные ошибки
 */
export const getAdminApiFieldErrors = (
  data?: unknown
): TAdminApiFieldErrors => {
  if (!data || typeof data !== 'object' || !('errors' in data)) return {};

  const errorData = data as IAdminErrorResponse;
  if (!errorData.errors || Array.isArray(errorData.errors)) return {};

  return Object.entries(errorData.errors).reduce<TAdminApiFieldErrors>(
    (acc, [field, messages]) => {
      if (!Array.isArray(messages)) return acc;

      return {
        ...acc,
        [field]: messages.map(String).filter(Boolean)
      };
    },
    {}
  );
};

export class AdminApiError extends Error {
  fieldErrors: TAdminApiFieldErrors;

  /**
   * Создаёт admin API error со структурированными ошибками полей.
   * @param data Данные ошибки
   */
  constructor(data?: unknown) {
    super(getAdminApiErrorMessage(data));
    this.name = 'AdminApiError';
    this.fieldErrors = getAdminApiFieldErrors(data);
  }
}

/**
 * Создаёт admin API error.
 * @param data Данные ошибки
 */
export const createAdminApiError = (data?: unknown) => {
  return new AdminApiError(data);
};

/**
 * Проверяет тип admin API error.
 * @param error Ошибка
 */
export const isAdminApiError = (error: unknown): error is AdminApiError => {
  return error instanceof AdminApiError;
};
